import { useCallback, useMemo } from 'react';

import { ApplyRewardsCard } from '#components/molecules/ApplyRewardsCard/ApplyRewardsCard.js';
import { useUserBalances } from '@givebacks/api/ledger';
import {
  type ConsumerCheckoutCartsResponse,
  useShowConsumerCheckout,
  useUpdateConsumerCheckoutRewards,
} from '@givebacks/api/store';

type ApplyRewardsProps = {
  checkoutId: string;
};

const selectRewardsData = (data: ConsumerCheckoutCartsResponse) => {
  return {
    isRewardsApplied: data.checkout?.apply_ledger_balance,
    isPledgeRequired: data.checkout?.pledge_required,
    ledgerAppliedAmount: data.checkout?.ledger_applied_amount,
    cartIds: data.checkout?.carts.map((cart) => cart.uuid),
    hasRecurringCarts: data.checkout?.carts.some((cart) => cart.recurring),
  };
};

const ApplyRewards = ({ checkoutId }: ApplyRewardsProps) => {
  const { data: balances, isLoading: isLoadingBalances } = useUserBalances();
  const { data: checkout, isLoading: isLoadingCheckout } = useShowConsumerCheckout({
    checkoutId,
    select: selectRewardsData,
  });

  const {
    mutate: updateCheckout,
    isPending: isUpdatingCheckout,
    variables: pendingVariables,
  } = useUpdateConsumerCheckoutRewards();

  const handleChange = useCallback(
    (selected: boolean) => {
      if (isUpdatingCheckout) return;

      updateCheckout({
        checkoutId,
        cartIds: checkout?.cartIds ?? [],
        checkout: {
          apply_ledger_balance: selected,
        },
      });
    },
    [checkoutId, isUpdatingCheckout, updateCheckout, checkout?.cartIds]
  );

  /** If the rewards are being applied or removed, return the reason */
  const isPendingReason = useMemo(() => {
    if (!isUpdatingCheckout) return undefined;
    if (pendingVariables?.checkout?.apply_ledger_balance) {
      return 'applying';
    }
    return 'removing';
  }, [isUpdatingCheckout, pendingVariables?.checkout?.apply_ledger_balance]);

  /** If the rewards are unavailable to apply, return the reason */
  const isUnavailableToApply = useMemo(() => {
    if (checkout?.isPledgeRequired) {
      return 'Unavailable for pledges';
    }
    if (checkout?.hasRecurringCarts) {
      return 'Unavailable for recurring donation';
    }
    return undefined;
  }, [checkout?.isPledgeRequired, checkout?.hasRecurringCarts]);

  return (
    <ApplyRewardsCard
      availableRewards={balances?.available ?? 0}
      selectedRewards={checkout?.ledgerAppliedAmount ?? 0}
      isInitialLoading={isLoadingBalances || isLoadingCheckout}
      isPending={isPendingReason}
      unavailableToApply={isUnavailableToApply}
      onChange={handleChange}
    />
  );
};

export { ApplyRewards };
